'use client'

import { useTranslations } from 'next-intl'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Tabs } from '@/components/tabs'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import {
    getRestaurantDetailScrollOffset,
    RestaurantDetailSectionId,
} from '@/features/restaurants/restaurant-detail/utils/restaurant-detail-helpers'
import { IOption } from '@/typings/option'
import { compact } from '@/utils/functions/compact'
import { throttle } from '@/utils/functions/throttle'

interface IRestaurantDetailNavigationProps {
    className?: string
}

export const RestaurantDetailNavigation = ({ className }: IRestaurantDetailNavigationProps) => {
    const { restaurant, sectionRefs } = useRestaurantDetailContext()

    const [selectedSectionId, rawSetSelectedSectionId] = useState<RestaurantDetailSectionId>(
        RestaurantDetailSectionId.DEALS
    )

    const t = useTranslations()

    const items = useMemo((): IOption<RestaurantDetailSectionId>[] | undefined => {
        if (!restaurant) return

        return compact([
            {
                label: t('restaurants.detail.navigation.deals'),
                value: RestaurantDetailSectionId.DEALS,
            },
            Boolean(restaurant.loyalty?.rewards?.length) && {
                label: t('restaurants.detail.navigation.rewards'),
                value: RestaurantDetailSectionId.REWARDS,
            },
            Boolean(restaurant.menuGroups?.length) && {
                label: t('restaurants.detail.navigation.menu'),
                value: RestaurantDetailSectionId.MENU,
            },
            {
                label: t('restaurants.detail.navigation.reviews'),
                value: RestaurantDetailSectionId.REVIEWS,
            },
            {
                label: t('restaurants.detail.navigation.info'),
                value: RestaurantDetailSectionId.INFO,
            },
        ])
    }, [restaurant, t])

    const ignoreUntil = useRef<RestaurantDetailSectionId | null>(null)

    const setSelectedOption = useCallback(
        (sectionId: RestaurantDetailSectionId) => {
            rawSetSelectedSectionId(sectionId)

            const targetSection = sectionRefs[sectionId]

            if (!targetSection) return

            ignoreUntil.current = sectionId

            window.scrollTo({
                behavior: 'smooth',
                top: Math.max(targetSection.offsetTop - getRestaurantDetailScrollOffset(), 0),
            })
        },
        [sectionRefs]
    )

    // For throttle to work we must return the same memoized throttled function
    // This is why we are using useMemo here, instead of useCallback
    const throttledOnScroll = useMemo(
        () =>
            throttle(() => {
                // This function checks which section is currently scrolled into view
                // According to that, it sets the selected section
                const scrollY = window.scrollY
                const sectionIds = Object.values(RestaurantDetailSectionId)

                for (const sectionId of sectionIds) {
                    const section = sectionRefs[sectionId]

                    if (!section) continue

                    // Account for the sticky header and some additional offset
                    const adjustedScrollY = scrollY + getRestaurantDetailScrollOffset()

                    // Substracting a few pixels from the top offset, so that it starts "earlier" than it actually is
                    // Otherwise the section would be selected too late and there are some visual glitches
                    const adjustedSectionOffsetTop = section.offsetTop - 200

                    // Check if the scroll position is below the start of the section
                    const isBelowThresholdStart = adjustedSectionOffsetTop < adjustedScrollY

                    // Check if the scroll position is above the end of the section
                    const isAboveThresholdEnd =
                        adjustedSectionOffsetTop + section.offsetHeight > scrollY

                    const isActive = isBelowThresholdStart && isAboveThresholdEnd

                    if (isActive) {
                        // Ignore scroll is true when the user has clicked on a section in the navigation and the browser is currently scrolling to that section
                        // Without ignoring the scroll, this function would select all sections that are being scrolled over in order to reach the target section
                        if (ignoreUntil.current) {
                            const isTargetSection = ignoreUntil.current === sectionId

                            if (!isTargetSection) continue

                            // When the browser eventually arrives at the target section, we want to stop ignoring the scroll
                            ignoreUntil.current = null
                        }

                        rawSetSelectedSectionId(() => sectionId)

                        return
                    }
                }

                const firstSectionId = sectionIds[0]
                const firstSection = sectionRefs[firstSectionId]

                if (!firstSection) return

                const isAboveFirstSection = scrollY < firstSection.offsetTop

                if (isAboveFirstSection) {
                    if (ignoreUntil.current) {
                        return
                    }

                    rawSetSelectedSectionId(() => firstSectionId)
                }
            }, 200),
        [sectionRefs]
    )

    // biome-ignore lint/correctness/useExhaustiveDependencies: deliberate scroll check
    useLayoutEffect(() => {
        // check if section is scrolled into view
        // if so, set the selected section
        window.addEventListener('scroll', () => throttledOnScroll())
    }, [sectionRefs, throttledOnScroll])

    if (!restaurant || !items) return null

    return (
        <Tabs
            selectedValue={selectedSectionId}
            onSelect={setSelectedOption}
            options={items}
            className={className}
        />
    )
}
